import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2, FileText, X } from "lucide-react";
import { api, apiError, uploadFiles } from "@/lib/api";
import { usePhotoInputs, PhotoUploadButton } from "@/components/shared/PhotoPicker";
import { SignedImg } from "@/components/shared/SignedImg";
import { formatCZK } from "@/lib/format";
import { projectLabel } from "@/lib/orders";

const RESULTS = [
  { key: "all", label: "Vše v pořádku", tone: "border-success bg-success/10 text-success" },
  { key: "partial", label: "Něco chybí", tone: "border-amber-500 bg-amber-500/10 text-amber-800 dark:text-amber-300" },
  { key: "damaged", label: "Poškozené", tone: "border-destructive bg-destructive/10 text-destructive" },
];

// Převzetí na stavbě (311): 1) doklad k převzetí, 2) přišlo všechno? Bez dokladu to nejde —
// kancelář pak vidí, že dodávka fyzicky dorazila, a při problému dostane zprávu hned.
// 332: doklad jde i nahrát jako PDF (často přijde e-mailem) — foto nebo PDF.
// 333: nemusí to být dodací list — stačí i faktura (dodavatel často dodací list nevystaví).
const isPdf = (u) => /\.pdf($|\?)/i.test(u || "");
export function ReceiveSheet({ order, onClose, onDone }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [result, setResult] = useState("all");
  const [missing, setMissing] = useState({});
  const [note, setNote] = useState("");
  const [paid, setPaid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { setPhotos([]); setResult("all"); setMissing({}); setNote(""); setPaid(false); setDone(null); }, [order?.id]);

  const onFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    setUploading({ done: 0, total: list.length });
    const r = await uploadFiles(list, { onEach: (d, t) => setUploading({ done: d, total: t }) });
    setUploading(null);
    if (r.urls.length) setPhotos((p) => [...p, ...r.urls]);
    if (r.failed.length) toast.error(apiError(r.failed[0].error) || "Soubor se nepodařilo nahrát.");
  };
  const pick = usePhotoInputs({ onChange: onFiles, disabled: !!uploading, pdf: true });

  if (!order) return null;
  const pickup = order.delivery?.mode === "pickup";
  const lines = order.lines || [];
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/orders/${order.id}/receive`, {
        photos, result, note, paid: pickup && paid,
        line_ids: result === "all" ? [] : Object.keys(missing).filter((k) => missing[k]),
      });
      setDone(r.data);
      onDone?.(r.data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!order} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent sheet className="w-[480px] max-w-[calc(100vw-2rem)] max-h-[92dvh] overflow-y-auto rounded-[22px]" data-testid="receive-sheet"
        onOpenAutoFocus={(e) => e.preventDefault()}>
        {done ? (
          <div className="py-6 text-center space-y-3" data-testid="receive-done">
            <CheckCircle2 size={44} className="mx-auto text-success" />
            <DialogTitle className="text-[22px] font-black">{result === "all" ? "Převzato" : "Převzato s problémem"}</DialogTitle>
            <DialogDescription className="text-[15px]">
              {result === "all" ? "Kancelář to vidí i s dokladem." : "Kancelář dostala zprávu hned — i s dokladem a poznámkou."}
            </DialogDescription>
            <Button onClick={onClose} className="w-full md:w-auto h-12 md:h-11 px-8 font-bold" data-testid="receive-close">Hotovo</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-[22px] font-black tracking-tight">{pickup ? "Vyzvednout" : "Převzít dodávku"}</DialogTitle>
              <DialogDescription>{order.supplier_name} · {order.title} · {projectLabel(order)}</DialogDescription>
            </DialogHeader>
            <div className="space-y-5">
              <section>
                <h3 className="text-[14px] font-extrabold mb-2">1. Vyfoť nebo nahraj doklad <span className="font-semibold text-muted-foreground">— dodací list, nebo fakturu</span></h3>
                {photos.length > 0 && (
                  <div className="flex gap-2 flex-wrap mb-2" data-testid="receive-photos">
                    {photos.map((u) => (
                      <span key={u} className="relative w-20 h-20 rounded-[12px] overflow-hidden border border-border" data-testid={isPdf(u) ? "receive-pdf" : undefined}>
                        {isPdf(u) ? (
                          <span className="w-full h-full bg-secondary grid place-items-center content-center text-muted-foreground">
                            <FileText size={26} aria-hidden="true" /><span className="text-[11px] font-bold">PDF</span>
                          </span>
                        ) : <SignedImg url={u} thumb={160} className="w-full h-full object-cover" />}
                        <button type="button" onClick={() => setPhotos((p) => p.filter((x) => x !== u))} aria-label="Odebrat soubor"
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white grid place-items-center"><X size={13} /></button>
                      </span>
                    ))}
                  </div>
                )}
                <PhotoUploadButton onClick={pick.open} busy={!!uploading} progress={uploading} testid="receive-photo-btn"
                  label={photos.length ? "Přidat další foto nebo PDF" : "Vyfotit nebo nahrát PDF"} hint={photos.length ? null : "foto nebo PDF · bez dokladu převzetí nejde"} />
                {pick.inputs}
              </section>

              <section>
                <h3 className="text-[14px] font-extrabold mb-2">2. Přišlo všechno v pořádku?</h3>
                <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Výsledek převzetí">
                  {RESULTS.map((r) => (
                    <button key={r.key} type="button" role="radio" aria-checked={result === r.key} onClick={() => setResult(r.key)} data-testid={`receive-${r.key}`}
                      className={`min-h-[48px] px-2 rounded-[14px] border text-[13.5px] font-bold transition-colors ${result === r.key ? r.tone : "border-border bg-card text-foreground"}`}>{r.label}</button>
                  ))}
                </div>
                {result !== "all" && (
                  <div className="mt-3 space-y-2">
                    {lines.length > 0 && (
                      <>
                        <p className="text-[12.5px] text-muted-foreground">Odškrtni, co nepřišlo nebo je poškozené.</p>
                        <ul className="rounded-[14px] border border-border divide-y divide-border/70" data-testid="receive-lines">
                          {lines.map((l) => (
                            <li key={l.id}>
                              <label className="flex items-center gap-3 px-3 min-h-[44px] cursor-pointer">
                                <Checkbox checked={!!missing[l.id]} onCheckedChange={(v) => setMissing((m) => ({ ...m, [l.id]: !!v }))} />
                                <span className="flex-1 min-w-0 text-[14px] truncate">{l.name}</span>
                                <span className="text-[12.5px] text-muted-foreground tabular-nums">{String(l.qty ?? 1).replace(".", ",")} {l.unit || "ks"}</span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="receive-note"
                      placeholder={result === "damaged" ? "Co je poškozené? Vyfoť to výše." : "Co chybí?"} className="text-base md:text-sm" />
                  </div>
                )}
                {result === "all" && (
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={1} placeholder="Poznámka (nepovinné)"
                    data-testid="receive-note" className="mt-3 text-base md:text-sm" />
                )}
              </section>

              {pickup && (
                <label className="flex items-center gap-3 rounded-[14px] border border-border px-3 min-h-[48px] cursor-pointer">
                  <Checkbox checked={paid} onCheckedChange={(v) => setPaid(!!v)} data-testid="receive-paid" />
                  <span className="text-[14px] font-semibold">Zaplaceno na pobočce</span>
                  {order.amount_gross != null && <span className="ml-auto tabular-nums text-[13.5px] font-bold">{formatCZK(order.amount_gross)}</span>}
                </label>
              )}

              <Button onClick={save} disabled={busy || !!uploading || !photos.length} data-testid="receive-confirm"
                className={`w-full h-12 font-bold ${result === "all" ? "" : "bg-destructive text-destructive-foreground hover:bg-destructive/90"}`}>
                {busy ? "Ukládám…" : !photos.length ? "Nejdřív přidej doklad" : result === "all" ? "Potvrdit převzetí" : "Převzít a nahlásit problém"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
